/**
 * EndScene — cinematic reward screen, shown after the city-building
 * completion animation + zoom finishes.
 *
 * Ported from balance-out's EndScene (same family look: dim cinema
 * backdrop, spotlight sweep, confetti burst, headline, then a reward
 * reveal with a sparkle burst, the prize icon, "Rewards earned!" and a
 * Play Again button). City-builder has no fail state, score or hints,
 * so the win path is the only path and those stats are dropped.
 *
 * Assets are loaded lazily by GameScene before this scene is launched.
 * endScreenBG and the reward icon (key 'rewardIcon') are placeholders
 * copied from balance-out — replace those PNGs with the city-specific
 * background and reward art.
 */

import { SFX, playSfx } from '@utils/SoundFX';

const WIDTH = 1280;
const HEIGHT = 720;

export default class EndScene extends Phaser.Scene {
  constructor() {
    super({ key: 'EndScene' });
  }

  create() {
    this.cameras.main.fadeIn(500, 255, 255, 255);

    this.add
      .sprite(WIDTH / 2, HEIGHT / 2, 'endScreenBG')
      .setDisplaySize(WIDTH, HEIGHT)
      .setDepth(0);

    // Spotlight sweep — opens ~2s in.
    this.spotlight = this.add.sprite(WIDTH / 2, HEIGHT / 2, 'spotlight').setDepth(1);
    this.anims.create({
      key: 'spotlightOpen',
      frames: this.anims.generateFrameNames('spotlight', {
        start: 0,
        end: 188,
        zeroPad: 0,
        prefix: 'spotlight0',
        suffix: '.png',
      }),
      frameRate: 20,
      repeat: 0,
    });
    this.time.delayedCall(2000, () => this.spotlight.play('spotlightOpen'), [], this);

    // Confetti burst.
    this.endConfetti = this.add.sprite(WIDTH / 2, HEIGHT / 2, 'endConfetti').setDepth(4);
    this.anims.create({
      key: 'endConfetti',
      frames: this.anims.generateFrameNames('endConfetti', {
        start: 0,
        end: 120,
        zeroPad: 0,
        prefix: 'seq_0_',
        suffix: '.png',
      }),
      frameRate: 40,
      repeat: 0,
    });
    this.endConfetti.play('endConfetti');

    // Headline.
    this.headline = this.add
      .sprite(WIDTH / 2, HEIGHT / 2, 'youWon')
      .setAlpha(0)
      .setDepth(2);
    this.tweens.add({
      targets: this.headline,
      ease: 'Expo.easeInOut',
      duration: 1000,
      delay: 300,
      alpha: 1,
    });
    this.tweens.add({
      targets: this.headline,
      ease: 'Linear',
      duration: 300,
      delay: 3000,
      y: HEIGHT / 2 - 50,
    });

    this.nextButton = this.add
      .sprite(WIDTH / 2, 740, 'endNextButton')
      .setAlpha(0)
      .setDepth(3)
      .setInteractive({ cursor: 'pointer' });
    this.tweens.add({
      targets: this.nextButton,
      ease: 'Linear',
      duration: 300,
      delay: 3000,
      alpha: 1,
      y: 650,
    });

    this.buildRewardReveal();

    this.nextButton.on('pointerover', () => this.nextButton.setTexture('nextHover'));
    this.nextButton.on('pointerout', () => this.nextButton.setTexture('endNextButton'));
    this.nextButton.on('pointerup', () => {
      playSfx(this, SFX.click);
      this.revealReward();
    });

    this.okButton.on('pointerover', () =>
      this.okButton.setTexture('playAgainButtonHover'),
    );
    this.okButton.on('pointerout', () => this.okButton.setTexture('playAgainButton'));
    this.okButton.on('pointerup', () => {
      playSfx(this, SFX.click);
      this.okButton.disableInteractive();
      this.handleReplay();
    });
  }

  // Build the (initially hidden) reward-reveal pieces.
  buildRewardReveal() {
    // Sparkle/burst background: frames Reward/Reward00..89 (zero-padded).
    // Plays once, then loops its tail (59..89) seamlessly.
    const REWARD_BG_LOOP_START = 59;
    this.rewardBg = this.add
      .sprite(WIDTH / 2, HEIGHT / 2, 'rewardBg', 'Reward/Reward00')
      .setAlpha(0)
      .setDepth(2);
    this.anims.create({
      key: 'rewardBgShow',
      frames: this.anims.generateFrameNames('rewardBg', {
        start: 0,
        end: 89,
        zeroPad: 2,
        prefix: 'Reward/Reward',
      }),
      frameRate: 30,
      repeat: 0,
    });
    this.anims.create({
      key: 'rewardBgLoop',
      frames: this.anims.generateFrameNames('rewardBg', {
        start: REWARD_BG_LOOP_START,
        end: 89,
        zeroPad: 2,
        prefix: 'Reward/Reward',
      }),
      frameRate: 24,
      repeat: -1,
    });
    this.rewardBg.on(
      Phaser.Animations.Events.ANIMATION_COMPLETE_KEY + 'rewardBgShow',
      () => this.rewardBg.play('rewardBgLoop'),
      this,
    );

    this.rewardIcon = this.add
      .image(WIDTH / 2, HEIGHT / 2, 'rewardIcon')
      .setAlpha(0)
      .setScale(0)
      .setDepth(3);

    this.okButton = this.add
      .sprite(WIDTH / 2, 710, 'playAgainButton')
      .setAlpha(0)
      .setDepth(4)
      .setInteractive({ cursor: 'pointer' });
  }

  // Headline → reward reveal.
  revealReward() {
    this.tweens.add({
      targets: [this.headline, this.nextButton],
      alpha: 0,
      duration: 300,
    });

    this.time.delayedCall(
      1000,
      () => {
        this.rewardBg.setAlpha(1).play('rewardBgShow');
        this.time.delayedCall(500, () => this.animateRewardIcon());

        this.tweens.add({
          targets: this.okButton,
          ease: 'Linear',
          duration: 300,
          delay: 3000,
          alpha: 1,
          y: 620,
        });

        this.add.sprite(577, 120, 'rewardEarned').setOrigin(0).setDepth(3);
        this.add
          .sprite(WIDTH / 2, HEIGHT / 2 + 170, 'rewardName')
          .setOrigin(0.5, 0.5)
          .setDepth(3);
      },
      [],
      this,
    );
  }

  // Prize icon pops in with an overshoot, then idles with a slow bob.
  animateRewardIcon() {
    const restY = HEIGHT / 2 - 40;
    const startY = restY + 70;
    this.rewardIcon.setPosition(WIDTH / 2, startY);
    this.tweens.add({
      targets: this.rewardIcon,
      alpha: 1,
      scale: 1,
      y: restY,
      duration: 700,
      ease: 'Back.easeOut',
      onComplete: () => {
        this.tweens.add({
          targets: this.rewardIcon,
          y: restY - 8,
          duration: 1200,
          ease: 'Sine.easeInOut',
          yoyo: true,
          repeat: -1,
        });
      },
    });
  }

  // Replay — fade to white, tear down and restart at StartScene.
  handleReplay() {
    this.cameras.main.fadeOut(500, 255, 255, 255);
    this.cameras.main.once('camerafadeoutcomplete', () => {
      this.scene.stop('GameScene');
      this.scene.stop('UIScene');
      this.scene.stop('EndScene');
      this.scene.start('StartScene');
    });
  }
}
